import { z } from "zod";
import { Id, IsoDateTime, Page, PageQuery } from "./common";
import { RiskLevel } from "./state";

export const AlertStatus = z.enum([
  "Active",
  "Acknowledged",
  "Dismissed",
  "Resolved",
]);
export type AlertStatus = z.infer<typeof AlertStatus>;

export const AlertSeverity = z.enum([
  "Critical",
  "High",
  "Medium",
  "Low",
  "Info",
]);
export type AlertSeverity = z.infer<typeof AlertSeverity>;

export const AlertSource = z.enum([
  "Rule",
  "Manual",
  "Integration",
  "Seed",
  "System",
]);
export type AlertSource = z.infer<typeof AlertSource>;

export const AlertEntityType = z.enum(["Asset", "Device", "Site"]);
export type AlertEntityType = z.infer<typeof AlertEntityType>;

export const Alert = z.object({
  alertId: Id,
  tenantId: Id,
  entityType: AlertEntityType,
  entityId: Id,
  siteId: Id.nullable(),
  buildingId: Id.nullable(),
  floorId: Id.nullable(),
  assetId: Id.nullable(),
  deviceId: Id.nullable(),
  title: z.string().min(1).max(160),
  description: z.string().max(4000).nullable(),
  severity: AlertSeverity,
  status: AlertStatus,
  source: AlertSource,
  /** External alert ID from integration provider. Used for deduplication in PollProcessor. */
  externalAlertId: z.string().min(1).max(256).nullable().optional(),
  /** Integration that produced this alert. */
  integrationId: Id.nullable().optional(),
  riskLevelAtCreation: RiskLevel.nullable(),
  reasonCodes: z.array(z.string().min(1).max(120)),
  acknowledgedAt: IsoDateTime.nullable(),
  acknowledgedBy: Id.nullable(),
  acknowledgedNote: z.string().max(2000).nullable(),
  dismissedAt: IsoDateTime.nullable(),
  dismissedBy: Id.nullable(),
  dismissedReason: z.string().max(2000).nullable(),
  resolvedAt: IsoDateTime.nullable(),
  resolvedBy: Id.nullable(),
  resolutionNote: z.string().max(2000).nullable(),
  linkedWorkItemIds: z.array(Id),
  /** Whether this alert can be resolved to a map location (has geometry from assignment, state lat/lng, or asset/site). */
  mapResolvable: z.boolean().optional(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
  updatedBy: Id.nullable(),
});
export type Alert = z.infer<typeof Alert>;

export const AlertListFilters = PageQuery.extend({
  status: AlertStatus.optional(),
  severity: AlertSeverity.optional(),
  siteId: Id.optional(),
  buildingId: Id.optional(),
  floorId: Id.optional(),
  assetId: Id.optional(),
  deviceId: Id.optional(),
  source: AlertSource.optional(),
  createdFrom: IsoDateTime.optional(),
  createdTo: IsoDateTime.optional(),
});
export type AlertListFilters = z.infer<typeof AlertListFilters>;

export const AlertCreateInput = z.object({
  entityType: AlertEntityType,
  entityId: Id,
  siteId: Id.nullable().optional(),
  buildingId: Id.nullable().optional(),
  floorId: Id.nullable().optional(),
  assetId: Id.nullable().optional(),
  deviceId: Id.nullable().optional(),
  title: z.string().min(1).max(160),
  description: z.string().max(4000).nullable().optional(),
  severity: AlertSeverity,
  source: AlertSource.default("Manual"),
  reasonCodes: z.array(z.string().min(1).max(120)).default([]),
});
export type AlertCreateInput = z.infer<typeof AlertCreateInput>;

export const AlertAcknowledgeInput = z.object({
  note: z.string().max(2000).optional(),
});
export type AlertAcknowledgeInput = z.infer<typeof AlertAcknowledgeInput>;

export const AlertDismissInput = z.object({
  reason: z.string().min(1).max(2000),
});
export type AlertDismissInput = z.infer<typeof AlertDismissInput>;

export const AlertResolveInput = z.object({
  resolutionNote: z.string().min(1).max(2000),
});
export type AlertResolveInput = z.infer<typeof AlertResolveInput>;

export const AlertLinkWorkItemInput = z.object({
  workItemId: Id.optional(),
  createWorkItem: z
    .object({
      title: z.string().min(1).max(160),
      description: z.string().max(4000).nullable().optional(),
      priority: z.enum(["Critical", "High", "Medium", "Low"]).optional(),
    })
    .optional(),
});
export type AlertLinkWorkItemInput = z.infer<typeof AlertLinkWorkItemInput>;

export const AlertListResponse = Page(Alert);
export type AlertListResponse = z.infer<typeof AlertListResponse>;
